//Library Imports
import React, { useState } from 'react'
import { useDrop } from "react-dnd";
import { useDispatch, useSelector } from 'react-redux';
//Local Imports
import AddRoundIcon from "assets/img/icons/addRoundIcon.svg"
import AddRoundGreyIcon from "assets/img/icons/addRoundGreyIcon.svg"
import { ItemTypes } from "modules/Common/Components/ItemTypes";
import { RootState } from "store";
// actions Imports
import { addNode, addActionModal, saveDropArea, clearCopyNode, moveNode, deleteNode, conditionConstraintModal } from "../actions";
import { ControlNameEnum, WFStatus, WFStatusEnum } from "../interfaces";
import { findEmailUsage, isChild } from "../utils";
//Style Imports
import styled, { keyframes } from "styled-components";


interface IAddMore {
  // clickHandler: () => void;
  disabled: boolean;
  dropHandler?: () => void;
  id: string;
  side?: string; // use for condition left or right side
  hasChildren?: boolean,
}

export default function AddMore(props: IAddMore) {
  const { disabled, dropHandler, id, side, hasChildren } = props;
  const copiedNode = useSelector((state: RootState) => state.automation.workflow.copiedNode);
  const workflowStatus: WFStatus = useSelector((state: RootState) => state.automation.workflow?.detail?.status);
  const dispatch = useDispatch();

  const isStatusNotDraft = workflowStatus !== WFStatusEnum.DRAFT;
  const [{ isOver, canDrop, item }, drop] = useDrop(() => ({
    accept: ItemTypes.CARD,
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
      item: monitor.getItem(),
    }),
    canDrop: (item) => {
      // if the dragged node is 'end' control and the drop node has children then
      // we restrict use to drop it.
      if (item.controlName === ControlNameEnum.END && hasChildren) {
        return false;
      } else if (isStatusNotDraft) {
        // if status if not "draft" then we restrict user to drop. 
        return false;
      }
      return true;
    },
    drop: (item: any) => {
      // id is the nodeId, where we have to put the new node/control
      let data = { id, ...item, side };
      if (item.hasOwnProperty('nodeId')) { // logic for moving the node
        // dispatch(moveNode({ ...data }));
        // in the following code we're checking that the node 'send email' should not go/move in the child of condition node in which the same email
        // is being used as pick in 'send email' node. 
        let usedEmailNodeId = item.controlName === ControlNameEnum.SEND_EMAIL && findEmailUsage(item.nodeId, data.emailId);
        // usedEmailNodeId should be the child of 'id' node. Because 'id' node is where the user want to move the node.
        if (usedEmailNodeId && !isChild(id, usedEmailNodeId[0])) {
          dispatch(conditionConstraintModal(true));
        } else {
          dispatch(addNode({ ...data }));
          dispatch(deleteNode(item.nodeId, 0));
        }
      } else {
        if (item?.controlName === ControlNameEnum.END) { // handle 'end' node functionality as it has no modal
          dispatch(addNode({ ...data }));
        } else {
          dispatch(addActionModal(true));
          dispatch(saveDropArea(data));
        }
      }
      dropHandler && dropHandler();
    },
  }), [id, hasChildren, isStatusNotDraft]); // these dependencies are added so that useDrop attributes/output i.e. isOver, canDrop, item
  // change if these dependencies got updated.

  const isActive = isOver && canDrop;
  const isNodeDisable = item?.controlName === ControlNameEnum.END && hasChildren;

  return (<AddMoreWrapper
    className={`${copiedNode.hasOwnProperty("nodeId") ? "copied" : disabled || isNodeDisable ? "active" : ""}`}
    ref={drop}
    onClick={() => {
      if (!isStatusNotDraft) {
        if (copiedNode.hasOwnProperty("nodeId")) {
          const { nodeId, ...obj } = copiedNode;
          dispatch(addNode({ id, ...obj, side }));
          dispatch(clearCopyNode());
        } else {
          dispatch(addActionModal(true));
          dispatch(dispatch(saveDropArea({ id, side })));
        }
      }
    }}
  >
    {isActive ? <DropPlace /> : <img src={isNodeDisable || isStatusNotDraft ? AddRoundGreyIcon : AddRoundIcon} alt="" />}
  </AddMoreWrapper >);
}

const growAnimation = keyframes`
  from {
    box-shadow: 0 0 0px 0px rgb(30 194 82 / 12%);
  }
  to {
    box-shadow: 0 0 20px 10px rgb(30 194 82 / 12%);
  }
`;

const AddMoreWrapper = styled.div`
  display: inline-block;
  margin: 0 auto;
  cursor: pointer;
  border-radius:50%;
  &.active{
    box-shadow: 0 0 20px 10px rgb(0 0 0 / 12%);
  }
  &.copied {
    animation: ${growAnimation} 0.75s infinite;
  }
  img{
    position:relative;
    z-index:-1;
  }
`;

const DropPlace = styled.div`
  position:relative;
  margin:0 auto;
  display:inline-block;
  &:before{
    content:"";
    width: 130px;
    height:45px;
    top: -23px;
    left: 2px;
    transform: translateX(-50%);
    border-radius:8px;
    border:1px dashed #7E7E7E;
    background-color: #E6F5EA;
    position:absolute;
    z-index:2;
  }
`;